// web/src/components/DrawEditor.tsx — סימון על תמונה / צילום מסך (28.09 · כלי ההערות v2 · מקביל ל-fbk-draw.js ברשגד, DEC 423).
// 🔑 עט · הדגשה · טקסט · מחק · 6 צבעים · 3 עוביים · ביטול צעד · איפוס. התמונה עצמה ⛔ נמחקת — המחק עובד רק על הקווים.
// 🔑 שלוש שכבות: התמונה · הקווים (קנבס) · הטקסטים (אלמנטים חיים). "סיום" מאחד הכל ל-JPEG אחד — רק אז הטקסט נצרב.
// 📄 מוטי 29.09: "הטקסט גדול מאוד. הוא גם צריך להיות בשכבה נפרדת ולאפשר עריכה שלו — הזזה, שינוי גודל או עריכת הטקסט —
//    רק בכפתור סיום אפשר לשטח את התמונה." · "'מדגש' — אין מילה כזו בעברית" ⇒ "הדגשה".
// 🔑 טקסט: נגיעה בתמונה (בכלי "טקסט") ⇒ תיבה חדשה בעריכה. גרירה = הזזה · העיגול בפינה = גודל · נגיעה שנייה / לחיצה כפולה = עריכה ·
//    ✕ או Delete = מחיקה. צבע ועובי שנבחרים כשטקסט מסומן — חלים עליו.
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPE } from "react";

type Tool = "pen" | "marker" | "text" | "eraser";
type Pt = { x: number; y: number };
type Stroke = { tool: "pen" | "marker" | "eraser"; color: string; w: number; pts: Pt[] };
// x = הקצה הימני (עברית נכתבת מימין), y = הקצה העליון. הכל בפיקסלים של התמונה המקורית.
type Txt = { id: string; x: number; y: number; size: number; color: string; text: string };
type Snap = { strokes: Stroke[]; texts: Txt[] };
type Drag = { id: string; mode: "move" | "size"; sx: number; sy: number; x0: number; y0: number; size0: number; w0: number;
  before: Snap; moved: boolean; wasSel: boolean };

const COLORS = ["#E5484D", "#F2B233", "#1F6FB2", "#2F8A4C", "#1C1C1C", "#FFFFFF"];
const WIDTHS = [3, 6, 12];
const TEXT_SCALE = [0.75, 1, 1.5];
const TOOLS: { k: Tool; label: string }[] = [
  { k: "pen", label: "עט" }, { k: "marker", label: "הדגשה" }, { k: "text", label: "טקסט" }, { k: "eraser", label: "מחק" },
];
const FONT = "Assistant, Arial, sans-serif";
const LINE = 1.25;
const outline = (c: string) => (c === "#FFFFFF" ? "#1C1C1C" : "#FFFFFF");
const blurActive = () => (document.activeElement as HTMLElement | null)?.blur();

export function DrawEditor({ image, onDone, onCancel }: { image: Blob; onDone: (b: Blob) => void; onCancel: () => void }) {
  const base = useRef<HTMLCanvasElement>(null);
  const over = useRef<HTMLCanvasElement>(null);
  const layers = useRef<HTMLDivElement>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [texts, setTexts] = useState<Txt[]>([]);
  const [hist, setHist] = useState<Snap[]>([]);
  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState<string>(COLORS[0] ?? "#E5484D");
  const [w, setW] = useState(1);
  const [sel, setSel] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [scale, setScale] = useState(1);                  // פיקסלי מסך לכל פיקסל בתמונה
  const cur = useRef<Stroke | null>(null);
  const drag = useRef<Drag | null>(null);

  // גודל טקסט ברירת מחדל: יחסי לרוחב התמונה (לא ענק כמו קודם — מוטי 29.09)
  const baseText = size ? Math.max(14, Math.round(size.w / 70)) : 24;
  const textSize = (i: number) => Math.round(baseText * (TEXT_SCALE[i] ?? 1));

  // טעינת התמונה לשכבה התחתונה
  useEffect(() => {
    (async () => {
      const bmp = await createImageBitmap(image);
      const c = base.current!, o = over.current!;
      c.width = o.width = bmp.width;
      c.height = o.height = bmp.height;
      c.getContext("2d")!.drawImage(bmp, 0, 0);
      setSize({ w: bmp.width, h: bmp.height });
    })().catch(() => undefined);
  }, [image]);

  // קנה המידה של התצוגה — כדי שהטקסט החי ייראה בדיוק כמו שייצרב
  useLayoutEffect(() => {
    const el = layers.current;
    if (!el || !size) return;
    const upd = () => setScale(el.clientWidth / size.w);
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, [size]);

  useEffect(() => { drawStrokes(strokes, null); }, [strokes, size]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Delete / Backspace על טקסט מסומן (לא בזמן עריכה)
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (!sel || editing) return;
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); removeText(sel); }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  });

  function remember(s: Snap = { strokes, texts }) { setHist((h) => [...h.slice(-49), s]); }
  function undo() {
    const last = hist[hist.length - 1];
    if (!last) return;
    setHist(hist.slice(0, -1));
    setStrokes(last.strokes); setTexts(last.texts); setSel(null); setEditing(null);
  }
  function resetAll() { remember(); setStrokes([]); setTexts([]); setSel(null); setEditing(null); }

  function drawStrokes(list: Stroke[], extra: Stroke | null) {
    const o = over.current;
    if (!o) return;
    const g = o.getContext("2d")!;
    g.clearRect(0, 0, o.width, o.height);
    const k = Math.max(1, Math.max(o.width, o.height) / 900);     // עובי יחסי לגודל התמונה
    for (const s of extra ? [...list, extra] : list) {
      g.save();
      g.globalCompositeOperation = s.tool === "eraser" ? "destination-out" : "source-over";
      g.globalAlpha = s.tool === "marker" ? 0.35 : 1;
      g.strokeStyle = s.color;
      g.lineWidth = s.w * k * (s.tool === "marker" || s.tool === "eraser" ? 3 : 1);
      g.lineCap = "round"; g.lineJoin = "round";
      g.beginPath();
      s.pts.forEach((p, i) => (i ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y)));
      const p0 = s.pts[0];
      if (s.pts.length === 1 && p0) g.lineTo(p0.x + 0.1, p0.y);
      g.stroke();
      g.restore();
    }
  }

  // ── קווים ──
  function pos(e: RPE<HTMLElement>): Pt {
    const r = over.current!.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * over.current!.width, y: ((e.clientY - r.top) / r.height) * over.current!.height };
  }
  function down(e: RPE<HTMLCanvasElement>) {
    e.preventDefault();
    if (editing) { blurActive(); return; }                  // נגיעה מחוץ לטקסט בזמן עריכה = סיום העריכה
    const p = pos(e);
    if (tool === "text") { addText(p); return; }
    setSel(null);
    over.current!.setPointerCapture(e.pointerId);
    cur.current = { tool, color, w: WIDTHS[w] ?? 6, pts: [p] };
    drawStrokes(strokes, cur.current);
  }
  function move(e: RPE<HTMLCanvasElement>) {
    if (!cur.current) return;
    cur.current.pts.push(pos(e));
    drawStrokes(strokes, cur.current);
  }
  function up() {
    const c = cur.current;
    if (!c) return;
    cur.current = null;
    remember();
    setStrokes((x) => [...x, c]);
  }

  // ── טקסט ──
  function addText(p: Pt) {
    const sz = textSize(w);
    const t: Txt = { id: crypto.randomUUID(), x: p.x, y: p.y - (sz * LINE) / 2, size: sz, color, text: "" };
    remember();
    setTexts((xs) => [...xs, t]);
    setSel(t.id); setEditing(t.id);
  }
  function removeText(id: string) {
    remember();
    setTexts((xs) => xs.filter((x) => x.id !== id));
    setSel(null); setEditing(null);
  }
  function endEdit(id: string, raw: string) {
    const text = raw.replace(/\u00a0/g, " ").replace(/^\s+|\s+$/g, "");
    setEditing(null);
    const prev = texts.find((x) => x.id === id);
    if (!prev) return;
    if (!text) { setTexts((xs) => xs.filter((x) => x.id !== id)); setSel(null); return; }   // טקסט ריק לא נשאר
    if (prev.text === text) return;
    if (prev.text) remember();                               // טקסט חדש כבר נרשם בהיסטוריה כשנוצר
    setTexts((xs) => xs.map((x) => (x.id === id ? { ...x, text } : x)));
  }
  function txtDown(e: RPE<HTMLElement>, t: Txt, mode: "move" | "size") {
    if (mode === "move" && editing === t.id) return;         // בזמן עריכה — לחיצה בתוך הטקסט מזיזה את הסמן
    e.stopPropagation(); e.preventDefault();
    if (editing) blurActive();
    const host = (mode === "size" ? e.currentTarget.parentElement : e.currentTarget) as HTMLElement;
    host.setPointerCapture(e.pointerId);
    drag.current = {
      id: t.id, mode, sx: e.clientX, sy: e.clientY, x0: t.x, y0: t.y, size0: t.size,
      w0: Math.max(20, host.getBoundingClientRect().width), before: { strokes, texts }, moved: false, wasSel: sel === t.id,
    };
    setSel(t.id);
  }
  function txtMove(e: RPE<HTMLElement>) {
    const d = drag.current;
    if (!d || !size) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 3) return;
    d.moved = true;
    setTexts((xs) => xs.map((x) => {
      if (x.id !== d.id) return x;
      if (d.mode === "move") return { ...x, x: d.x0 + dx / scale, y: d.y0 + dy / scale };
      // העיגול בפינה השמאלית-תחתונה: גרירה שמאלה מגדילה (הקצה הימני נשאר במקום)
      return { ...x, size: Math.min(size.w / 5, Math.max(10, d.size0 * ((d.w0 - dx) / d.w0))) };
    }));
  }
  function txtUp() {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.moved) remember(d.before);
    else if (d.mode === "move" && d.wasSel) setEditing(d.id); // נגיעה שנייה בטקסט שכבר מסומן = עריכה
  }
  function pickColor(c: string) {
    setColor(c);
    const t = texts.find((x) => x.id === sel);
    if (t && t.color !== c) { remember(); setTexts((xs) => xs.map((x) => (x.id === t.id ? { ...x, color: c } : x))); }
  }
  function pickWidth(i: number) {
    setW(i);
    const t = texts.find((x) => x.id === sel);
    if (t) { remember(); setTexts((xs) => xs.map((x) => (x.id === t.id ? { ...x, size: textSize(i) } : x))); }
  }

  // ── סיום: איחוד שלוש השכבות ל-JPEG אחד ──
  async function finish() {
    if (editing) blurActive();
    setBusy(true);
    const b = base.current!, o = over.current!;
    const out = document.createElement("canvas");
    out.width = b.width; out.height = b.height;
    const g = out.getContext("2d")!;
    g.drawImage(b, 0, 0);
    g.drawImage(o, 0, 0);
    await document.fonts?.ready;
    for (const t of texts) {
      g.save();
      g.font = `700 ${t.size}px ${FONT}`;
      g.direction = "rtl"; g.textAlign = "right"; g.textBaseline = "top";
      g.lineJoin = "round"; g.lineWidth = Math.max(2, t.size * 0.14);
      g.strokeStyle = outline(t.color); g.fillStyle = t.color;
      t.text.split("\n").forEach((ln, i) => {
        const y = t.y + i * t.size * LINE + (t.size * (LINE - 1)) / 2;
        g.strokeText(ln, t.x, y);
        g.fillText(ln, t.x, y);
      });
      g.restore();
    }
    const blob = await new Promise<Blob | null>((ok) => out.toBlob(ok, "image/jpeg", 0.88));
    setBusy(false);
    if (blob) onDone(blob);
  }

  const W = size?.w ?? 1, H = size?.h ?? 1;

  return (
    <div className="draw" role="dialog" aria-label="סימון על התמונה">
      <div className="draw-top">
        <button className="btn btn-quiet draw-light" onClick={onCancel}>ביטול</button>
        <strong>סמני על התמונה</strong>
        <button className="btn btn-primary btn-sm draw-done" disabled={busy}
                onPointerDown={() => { if (editing) blurActive(); }}   // העריכה נסגרת ונשמרת לפני האיחוד
                onClick={() => void finish()}>סיום</button>
      </div>
      <div className="draw-stage">
        <div ref={layers} className="draw-layers" style={size ? { aspectRatio: `${size.w} / ${size.h}` } : undefined}>
          <canvas ref={base} className="draw-base" />
          <canvas ref={over} className={`draw-over tool-${tool}`} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
          {size && texts.map((t) => (
            <div key={t.id} className={`draw-txt${sel === t.id ? " is-sel" : ""}${editing === t.id ? " is-edit" : ""}`}
                 style={{ right: `${((W - t.x) / W) * 100}%`, top: `${(t.y / H) * 100}%`, fontSize: `${t.size * scale}px`, color: t.color,
                          ["--ol" as string]: outline(t.color) }}
                 onPointerDown={(e) => txtDown(e, t, "move")} onPointerMove={txtMove} onPointerUp={txtUp} onPointerCancel={txtUp}
                 onDoubleClick={() => { setSel(t.id); setEditing(t.id); }}>
              {editing === t.id
                ? <Editable initial={t.text} onDone={(s) => endEdit(t.id, s)} />
                : <span>{t.text}</span>}
              {sel === t.id && editing !== t.id && (
                <>
                  <button type="button" className="draw-txt-del" aria-label="מחיקת הטקסט"
                          onPointerDown={(e) => e.stopPropagation()} onClick={() => removeText(t.id)}>✕</button>
                  <span className="draw-txt-size" role="presentation" title="גרירה לשינוי גודל"
                        onPointerDown={(e) => txtDown(e, t, "size")} />
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="draw-bar">
        <div className="row-wrap draw-row" role="group" aria-label="כלי">
          {TOOLS.map((x) => (
            <button key={x.k} className={`draw-chip${tool === x.k ? " on" : ""}`} aria-pressed={tool === x.k} onClick={() => setTool(x.k)}>{x.label}</button>
          ))}
        </div>
        {tool === "text" && (
          <p className="draw-hint">געי בתמונה כדי להוסיף טקסט. גרירה מזיזה · העיגול בפינה משנה גודל · נגיעה נוספת בטקסט — עריכה.</p>
        )}
        <div className="row-wrap draw-row" role="group" aria-label="צבע">
          {COLORS.map((c) => (
            <button key={c} className={`draw-color${color === c ? " on" : ""}`} style={{ background: c }} aria-label={`צבע ${c}`} aria-pressed={color === c} onClick={() => pickColor(c)} />
          ))}
        </div>
        <div className="row-wrap draw-row" role="group" aria-label="עובי">
          {WIDTHS.map((x, i) => (
            <button key={x} className={`draw-chip${w === i ? " on" : ""}`} aria-pressed={w === i} onClick={() => pickWidth(i)} aria-label={["דק", "בינוני", "עבה"][i]}>
              <span className="draw-dot" style={{ width: 4 + i * 5, height: 4 + i * 5 }} />
            </button>
          ))}
          <button className="draw-chip" disabled={!hist.length} onClick={undo}>ביטול צעד</button>
          <button className="draw-chip" disabled={!strokes.length && !texts.length} onClick={resetAll}>איפוס</button>
        </div>
      </div>
    </div>
  );
}

// תיבת עריכה חיה: הטקסט הקיים מסומן כולו, Escape או נגיעה בחוץ = סיום.
function Editable({ initial, onDone }: { initial: string; onDone: (text: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerText = initial;
    el.focus();
    const r = document.createRange();
    r.selectNodeContents(el);
    const s = window.getSelection();
    s?.removeAllRanges(); s?.addRange(r);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={ref} className="draw-txt-edit" contentEditable suppressContentEditableWarning dir="rtl" role="textbox" aria-label="טקסט על התמונה"
         onBlur={() => onDone(ref.current?.innerText ?? "")}
         onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Escape") { e.preventDefault(); ref.current?.blur(); } }} />
  );
}
